import { FC, useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { ActorType } from 'features/new-actor/enums/actorType';
import { GenericLoading } from 'components/ui/Loader';
import { QuickSurvey } from 'features/subscription-process/profiling-process/components/Survey/QuickSurvey';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay';
import { BorrowerActorSelector } from '../borrower-insured/shared/BorrowerActorSelector';
import { MinimalNaturalPersonLayout } from './MinimalNaturalPersonLayout';
import { MinimalLegalEntityLayout } from './MinimalLegalEntityLayout';
import {
  useMinimalBorrowerForm,
  MinimalBorrowerActorType
} from './useMinimalBorrowerForm';
import {
  MinimalNaturalPersonFormValues,
  MinimalLegalEntityFormValues,
  minimalNaturalPersonFormConfig,
  minimalLegalEntityFormConfig
} from './minimalBorrowerForm';

// Questions to display in minimal form (others are hidden but keep default answers)
const VISIBLE_QUESTION_KEYS = [
  'businessTravel', // Déplacements professionnels
  'heavyLifting', // Charges
  'heightWork', // Travaux en hauteur
  'smoker', // Fumeur
  'socialCategory', // CSP
  'riskProfession' // profession a risque
];

interface MinimalBorrowerFormProps {
  onSuccess?: (actorId: string) => void;
  defaultActorType?: MinimalBorrowerActorType;
  personId?: string;
  excludeIds?: string[];
  /**
   * Id du projet d'emprunt fourni explicitement par l'ouvrant (comparateur / profilage),
   * qui le détient déjà. Fiable même quand le panneau est rendu hors du sous-arbre
   * `<Route>` ; sinon on retombe sur l'URL (useBorrowerProjectId).
   */
  borrowerProjectId?: string;
}

const ValidationTracker = ({
  isValid,
  onValidationChange
}: {
  isValid: boolean;
  onValidationChange: (isValid: boolean) => void;
}) => {
  useEffect(() => {
    onValidationChange(isValid);
  }, [isValid, onValidationChange]);
  return null;
};

// Remonte la civilité live du Formik vers le parent pour piloter l'avatar du selector.
const CivilityWatcher = ({
  civility,
  onCivilityChange
}: {
  civility?: string;
  onCivilityChange: (civility?: string) => void;
}) => {
  useEffect(() => {
    onCivilityChange(civility);
  }, [civility, onCivilityChange]);
  return null;
};

export const MinimalBorrowerForm: FC<MinimalBorrowerFormProps> = ({
  onSuccess,
  defaultActorType = ActorType.BorrowerNatural,
  personId,
  excludeIds = [],
  borrowerProjectId
}) => {
  const { t } = useTranslation(NsKey.Common);

  const {
    actorType,
    setActorType,
    isNaturalPerson,
    isEdit,
    isLoading,
    isSubmitting,
    initialValues,
    formUniqueKey,
    naturalPersonFormikRef,
    legalEntityFormikRef,
    handleNaturalPersonSubmit,
    handleLegalEntitySubmit,
    handleValidationChange,
    handleSurveyAnswersChange,
    surveyId,
    questions,
    existingAnswers,
    isDeleteConfirmOpen,
    closeDeleteConfirm,
    handleConfirmDelete
  } = useMinimalBorrowerForm({ onSuccess, defaultActorType, personId, borrowerProjectId });

  // Civilité live (dropdown) pour piloter l'avatar du selector, qui est hors Formik.
  const [currentCivility, setCurrentCivility] = useState<string | undefined>();

  if (isLoading) {
    return <GenericLoading isPageCentered loading />;
  }

  return (
    <div className="relative flex flex-col gap-6">
      {isSubmitting && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 rounded-lg">
          <GenericLoading loading />
        </div>
      )}

      <div className="flex items-center">
        <BorrowerActorSelector
          actorType={actorType}
          isEdit={isEdit}
          excludeIds={excludeIds}
          currentCivility={currentCivility}
          onChange={(type: ActorType) =>
            setActorType(type as MinimalBorrowerActorType)
          }
        />
      </div>

      {isNaturalPerson ? (
        <Formik<MinimalNaturalPersonFormValues>
          key={formUniqueKey}
          enableReinitialize
          initialValues={initialValues as MinimalNaturalPersonFormValues}
          validationSchema={minimalNaturalPersonFormConfig.validationSchema}
          onSubmit={handleNaturalPersonSubmit}
          innerRef={naturalPersonFormikRef}
        >
          {({ isValid, values }) => (
            <>
              <ValidationTracker
                isValid={isValid}
                onValidationChange={handleValidationChange}
              />
              <CivilityWatcher
                civility={values.civility}
                onCivilityChange={setCurrentCivility}
              />
              <Form className="flex flex-col gap-6">
                <MinimalNaturalPersonLayout />

                {/* Questionnaire section */}
                {surveyId && questions.length > 0 && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {t('COMMON.LABELS.SITUATION')}
                    </h3>
                    <QuickSurvey
                      surveyId={surveyId}
                      questions={questions}
                      existingAnswers={existingAnswers}
                      defaultAnswerIndex={0}
                      onChange={handleSurveyAnswersChange}
                      visibleQuestionKeys={VISIBLE_QUESTION_KEYS}
                    />
                  </div>
                )}
              </Form>
            </>
          )}
        </Formik>
      ) : (
        <Formik<MinimalLegalEntityFormValues>
          key={formUniqueKey}
          enableReinitialize
          initialValues={initialValues as MinimalLegalEntityFormValues}
          validationSchema={minimalLegalEntityFormConfig.validationSchema}
          onSubmit={handleLegalEntitySubmit}
          innerRef={legalEntityFormikRef}
        >
          {({ isValid }) => (
            <>
              <ValidationTracker
                isValid={isValid}
                onValidationChange={handleValidationChange}
              />
              <Form className="flex flex-col gap-6">
                <MinimalLegalEntityLayout />

                {/* Questionnaire section */}
                {surveyId && questions.length > 0 && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {t('COMMON.LABELS.SITUATION')}
                    </h3>
                    <QuickSurvey
                      surveyId={surveyId}
                      questions={questions}
                      existingAnswers={existingAnswers}
                      defaultAnswerIndex={0}
                      onChange={handleSurveyAnswersChange}
                      visibleQuestionKeys={VISIBLE_QUESTION_KEYS}
                    />
                  </div>
                )}
              </Form>
            </>
          )}
        </Formik>
      )}

      <ConfirmationOverlay
        isOpen={isDeleteConfirmOpen}
        onClose={closeDeleteConfirm}
        onConfirm={handleConfirmDelete}
        title="COMMON.DELETE_CONFIRMATION_TITLE"
        description="COMMON.DELETE_CONFIRMATION_DESCRIPTION"
        confirmLabel="COMMON.DELETE"
        variant="danger"
      />
    </div>
  );
};
